import { useEffect, useState } from "react";
import { AlertTriangle, Upload, CheckCircle2, Download, FileText, Video, X, Youtube, Instagram, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DOC_TYPES = [
  { key: "aadhar", label: "Aadhar Card" },
  { key: "birth_certificate", label: "Birth Certificate" },
  { key: "school_id", label: "School ID" },
];
const KIND_ICON = { youtube: Youtube, instagram: Instagram, link: LinkIcon };

export const PortalDocs = ({ playerId, documents, onChanged }) => {
  const upload = async (docKey, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/documents/${playerId}/${docKey}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div id="docs" className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="portal-docs">
      <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-4">Player Documents</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {DOC_TYPES.map((d) => {
          const has = !!(documents && documents[d.key]);
          return (
            <div key={d.key} className={`border ${has ? "border-[#D4AF37]/40" : "border-white/10"} p-4`} data-testid={`portal-doc-${d.key}`}>
              <div className="flex items-center gap-2 mb-3">
                <FileText size={14} className={has ? "text-[#D4AF37]" : "text-white/30"} />
                <p className="text-sm font-semibold">{d.label}</p>
              </div>
              {has ? (
                <div className="flex flex-col gap-2">
                  <a href={`${BACKEND_URL}/api/documents/${playerId}/${d.key}`} target="_blank" rel="noreferrer"
                    className="text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1.5 hover:bg-[#D4AF37] hover:text-black inline-flex items-center gap-1.5 justify-center"
                    data-testid={`portal-doc-view-${d.key}`}>
                    <Download size={11} /> View
                  </a>
                  <label className="text-[10px] uppercase tracking-widest text-white/50 hover:text-white border border-white/10 px-3 py-1.5 cursor-pointer inline-flex items-center gap-1.5 justify-center">
                    <Upload size={11} /> Replace
                    <input type="file" accept="image/*,application/pdf" onChange={(e) => upload(d.key, e.target.files?.[0])} className="hidden" data-testid={`portal-doc-upload-${d.key}`} />
                  </label>
                </div>
              ) : (
                <label className="text-[10px] uppercase tracking-widest text-black bg-[#D4AF37] hover:bg-[#F3D059] px-3 py-2 cursor-pointer inline-flex items-center gap-1.5 justify-center w-full">
                  <Upload size={11} /> Upload
                  <input type="file" accept="image/*,application/pdf" onChange={(e) => upload(d.key, e.target.files?.[0])} className="hidden" data-testid={`portal-doc-upload-${d.key}`} />
                </label>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const DocsDisclaimer = ({ documents }) => {
  const missing = DOC_TYPES.filter((d) => !documents || !documents[d.key]);
  if (missing.length === 0) return null;
  return (
    <a href="#docs" className="flex items-center gap-3 border border-orange-400/50 bg-orange-500/10 p-4 mb-8 hover:bg-orange-500/20 transition-colors duration-150" data-testid="portal-docs-disclaimer">
      <AlertTriangle size={18} className="text-orange-300 shrink-0" />
      <p className="text-sm text-orange-100">
        Please upload {missing.map((m) => m.label).join(", ")} to complete your player profile.
        <span className="text-[#D4AF37] ml-2 underline">Go to upload →</span>
      </p>
    </a>
  );
};

export const PortalWorkouts = () => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Plyometrics");
  const [subTab, setSubTab] = useState("");
  const [dedicatedOpen, setDedicatedOpen] = useState(false);

  useEffect(() => {
    api.get("/portal/workouts").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const categories = Object.keys(data.categories || {});
  const subs = data.categories?.[tab] || [];
  const visible = data.library.filter((v) => v.category === tab && (subs.length === 0 || !subTab || v.subcategory === subTab));

  const VideoCard = ({ v }) => {
    const Icon = KIND_ICON[v.kind] || LinkIcon;
    return (
      <div className="border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid={`portal-workout-${v.id}`}>
        <div className="aspect-video bg-black">
          {v.embed_url ? (
            <iframe title={v.title} src={v.embed_url} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen loading="lazy" className="w-full h-full" />
          ) : (
            <a href={v.url} target="_blank" rel="noreferrer" className="flex items-center justify-center h-full text-[#D4AF37]"><LinkIcon size={20} /></a>
          )}
        </div>
        <div className="p-3">
          <p className="text-sm font-semibold truncate">{v.title}</p>
          <p className="text-white/40 text-xs mt-0.5 flex items-center gap-1"><Icon size={11} /> {v.category}{v.subcategory ? ` · ${v.subcategory}` : ""}</p>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="portal-workouts">
      <div className="flex gap-2 flex-wrap mb-4">
        {categories.map((c) => (
          <button key={c} onClick={() => { setTab(c); setSubTab(""); setDedicatedOpen(false); }}
            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors duration-200 ${!dedicatedOpen && tab === c ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60 hover:border-[#D4AF37]"}`}
            data-testid={`portal-workout-tab-${c.toLowerCase()}`}>{c}</button>
        ))}
        {data.dedicated.length > 0 && (
          <button onClick={() => setDedicatedOpen(true)}
            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors duration-200 ${dedicatedOpen ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"}`}
            data-testid="portal-workout-tab-dedicated">
            Specially For You ({data.dedicated.length})
          </button>
        )}
      </div>
      {!dedicatedOpen && subs.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-4">
          {["", ...subs].map((s) => (
            <button key={s || "all"} onClick={() => setSubTab(s)}
              className={`text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors duration-150 ${subTab === s ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-white/50 hover:border-[#D4AF37]/60"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
      )}
      {dedicatedOpen ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {data.dedicated.map((v) => <VideoCard key={v.id} v={v} />)}
        </div>
      ) : visible.length === 0 ? (
        <p className="text-white/40 text-sm border border-white/10 p-8 text-center">No videos in {tab}{subTab ? ` · ${subTab}` : ""} yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {visible.map((v) => <VideoCard key={v.id} v={v} />)}
        </div>
      )}
    </div>
  );
};

export const PortalPolicies = () => {
  const [policy, setPolicy] = useState(null);
  useEffect(() => {
    api.get("/policies").then((r) => setPolicy(r.data && r.data.id ? r.data : null)).catch(() => {});
  }, []);
  if (!policy) return null;
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid="portal-policies">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Zavi Policies</p>
          <p className="text-white/60 text-sm mt-1">{policy.filename}</p>
        </div>
        <a href={`${BACKEND_URL}/api/policies/file`} target="_blank" rel="noreferrer"
          className="flex items-center gap-2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded-sm hover:bg-[#F3D059]"
          data-testid="portal-policies-view">
          <Download size={13} /> View / Download
        </a>
      </div>
    </div>
  );
};
